import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { LANGS, dict, t as translate } from './translations';

const STORAGE_KEY = 'tf-lang';
const DEFAULT_LANG = 'en';

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (k) => k,
  isCy: false,
  langs: LANGS,
});

function readInitialLang() {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && dict[stored]) return stored;
  } catch {
    // ignore
  }
  // Auto-pick Welsh only if the browser explicitly asks for it AND the user
  // hasn't chosen anything yet. Everything else defaults to English.
  const navLangs = (navigator.languages || [navigator.language || '']).map((l) =>
    l.toLowerCase(),
  );
  if (navLangs.some((l) => l.startsWith('cy'))) return 'cy';
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readInitialLang);

  useEffect(() => {
    const conf = LANGS.find((l) => l.code === lang) || LANGS[0];
    document.documentElement.lang = conf.htmlLang;
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang: (next) => (dict[next] ? setLangState(next) : null),
      t: (key) => translate(lang, key),
      isCy: lang === 'cy',
      langs: LANGS,
    }),
    [lang],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
